import { supabase } from '@/integrations/supabase/client';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
}

export interface TrafficSource {
  source: string;
  medium: string;
  campaign?: string;
  sessions: number;
  conversions: number;
  conversionRate: number;
}

// Extract UTM parameters from URL
export const extractUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  return {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    referrer: referrer || undefined,
  };
};

// Store UTM params in session storage for later use
export const storeUTMParams = (params: UTMParams) => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem('utm_params', JSON.stringify(params));
  } catch (error) {
    console.error('Error storing UTM params:', error);
  }
};

// Get stored UTM params
export const getStoredUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error getting stored UTM params:', error);
    return {};
  }
};

// Track analytics event with UTM data
export const trackEventWithUTM = async (
  eventType: string,
  userId?: string,
  additionalData?: Record<string, any>
) => {
  const utmParams = getStoredUTMParams();
  
  try {
    const { error } = await supabase
      .from('analytics_events')
      .insert({
        user_id: userId,
        event_type: eventType,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        referrer_url: utmParams.referrer,
        page_url: window.location.href,
        metadata: additionalData || {},
        device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
        browser_info: navigator.userAgent,
      });

    if (error) {
      console.error('Error tracking UTM event:', error);
    }
  } catch (error) {
    console.error('Error in trackEventWithUTM:', error);
  }
};

// Get traffic sources analytics
export const getTrafficSourcesAnalytics = async (timeRange: string = 'month'): Promise<TrafficSource[]> => {
  try {
    const now = new Date();
    const startDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default: // month
        startDate.setMonth(now.getMonth() - 1);
    }

    // Get analytics events
    const { data: events, error } = await supabase
      .from('analytics_events')
      .select('*')
      .gte('created_at', startDate.toISOString());

    if (error) {
      console.error('Error fetching traffic sources:', error);
      return [];
    }

    if (!events || events.length === 0) {
      return [];
    }

    // Group by source and medium
    const sourceStats = new Map<string, {
      sessions: number;
      conversions: number;
      medium: string;
      campaign?: string;
    }>();

    events.forEach((event: any) => {
      const source = event.utm_source || inferSourceFromReferrer(event.referrer_url) || 'direct';
      const medium = event.utm_medium || inferMediumFromReferrer(event.referrer_url) || 'none';
      const campaign = event.utm_campaign;
      
      const key = `${source}_${medium}`;
      
      if (!sourceStats.has(key)) {
        sourceStats.set(key, {
          sessions: 0,
          conversions: 0,
          medium,
          campaign
        });
      }

      const stats = sourceStats.get(key)!;
      
      if (event.event_type === 'page_view') {
        stats.sessions++;
      } else if (event.event_type === 'booking_complete') {
        stats.conversions++;
      }
    });

    // Convert to array and calculate conversion rates
    return Array.from(sourceStats.entries())
      .map(([key, stats]) => {
        const source = key.split('_')[0];
        return {
          source,
          medium: stats.medium,
          campaign: stats.campaign,
          sessions: stats.sessions,
          conversions: stats.conversions,
          conversionRate: stats.sessions > 0 ? (stats.conversions / stats.sessions) * 100 : 0
        };
      })
      .filter(item => item.sessions > 0)
      .sort((a, b) => b.sessions - a.sessions);

  } catch (error) {
    console.error('Error in getTrafficSourcesAnalytics:', error);
    return [];
  }
};

// Infer source from referrer URL
const inferSourceFromReferrer = (referrer?: string): string | null => {
  if (!referrer) return null;

  const domain = new URL(referrer).hostname.toLowerCase();
  
  if (domain.includes('xiaohongshu') || domain.includes('xhs')) return 'xiaohongshu';
  if (domain.includes('instagram') || domain.includes('ig')) return 'instagram';
  if (domain.includes('facebook')) return 'facebook';
  if (domain.includes('twitter') || domain.includes('x.com')) return 'twitter';
  if (domain.includes('linkedin')) return 'linkedin';
  if (domain.includes('youtube')) return 'youtube';
  if (domain.includes('bilibili')) return 'bilibili';
  if (domain.includes('google')) return 'google';
  if (domain.includes('baidu')) return 'baidu';
  if (domain.includes('weibo')) return 'weibo';
  
  return 'referral';
};

// Infer medium from referrer URL
const inferMediumFromReferrer = (referrer?: string): string | null => {
  if (!referrer) return null;

  const domain = new URL(referrer).hostname.toLowerCase();
  
  if (domain.includes('google') || domain.includes('baidu') || domain.includes('bing')) {
    return 'organic';
  }
  
  if (domain.includes('facebook') || domain.includes('instagram') || 
      domain.includes('twitter') || domain.includes('linkedin') || 
      domain.includes('xiaohongshu') || domain.includes('bilibili') || 
      domain.includes('weibo') || domain.includes('youtube')) {
    return 'social';
  }
  
  return 'referral';
};

// Initialize UTM tracking on page load
export const initializeUTMTracking = () => {
  if (typeof window === 'undefined') return;

  // Extract and store UTM params on page load
  const utmParams = extractUTMParams();
  if (Object.keys(utmParams).length > 0) {
    storeUTMParams(utmParams);
  }

  // Track page view with UTM data
  trackEventWithUTM('page_view');
};